import { create } from 'zustand';
import { isEqual, mergeWith } from 'lodash-es';
import { useApiClient } from '@/app/core/api-client';
import { config } from '@/app/core/config';

interface UiStateStore<T> {
  state: T;
  fetching: boolean;
  fetched: boolean;
  fetch: () => Promise<void>;
  setState: (partial: Partial<T>) => void;
}

export function createUiStateStore<T extends object>(key: string, defaults: T) {
  let debounceTimer: NodeJS.Timeout | null = null;
  let lastSavedState: T = defaults;

  return create<UiStateStore<T>>((set, get) => ({
    state: defaults,
    fetching: false,
    fetched: false,

    fetch: async () => {
      const apiClient = useApiClient();
      set({ fetching: true });
      try {
        const response = await apiClient.transport.get<{ data: { value: T }[] }>(`/v1/user/ui_states`, {
          baseURL: config.bffUrl,
          params: { key },
        });
        const persisted = response.data?.data?.[0]?.value;
        /**
         * API may return a partial document (e.g. omit `columns`). Shallow replace would drop
         * nested defaults and break controlled UI (e.g. SelectPanel visibility toggles).
         * Arrays are replaced wholesale so lists like `records` merge correctly.
         */
        const value = persisted
          ? (mergeWith({}, defaults, persisted, (objValue: unknown, srcValue: unknown) => {
              if (Array.isArray(srcValue)) return srcValue;
              if (srcValue === null) return objValue;
              return undefined;
            }) as T)
          : defaults;
        lastSavedState = value;
        set({ state: value, fetched: true, fetching: false });
      } catch {
        set({ state: defaults, fetched: true, fetching: false });
      }
    },

    setState: (partial) => {
      const newState = { ...get().state, ...partial };
      set({ state: newState });

      if (!get().fetched) return; // Don't persist until initial fetch completes

      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(async () => {
        if (isEqual(newState, lastSavedState)) return;
        const apiClient = useApiClient();
        try {
          await apiClient.transport.post(
            `/v1/user/ui_states`,
            { key, value: newState },
            {
              baseURL: config.bffUrl,
            },
          );
          lastSavedState = newState;
        } catch {
          // Silent fail - user can retry
        }
      }, 3000);
    },
  }));
}
